/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AnyAction, Dispatch } from "redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { AxiosAction } from "order-manager/types/api/common.type"
import { FetchStoreListResponse } from "order-manager/types/api/orderFetchStoreList.type"
import { sleep } from "utils/misc"
import { storageHelper } from "utils/storage"
import { getResponseErrorEventReason } from "utils/tracking"
import { Config } from "constants/config"
import { getGrabXValueFromLocalStorage } from "grabx/getter"
import { MerchantPortalFeatureFlag } from "constants/features"

// singleton mutex to prevent duplicate dispatches
// issue: effects may be ran with old values from the store, esp. if they belong in the same render cycle
let isLoading = false

export interface Labels {
  LOAD_TIME: number
  LOADING_STATUS: string
  REASON: string
  ERROR_MSG: string
}

export const fetchAllStoreListFactory = (
  dispatch: Dispatch,
  isFetchingOutlets: boolean,
) => async ({
  disableGlobalLoading,
  stopAfterFirstPage,
  onFetched,
}: {
  disableGlobalLoading?: boolean
  stopAfterFirstPage?: boolean
  onFetched?: (labels: Labels) => void
} = {}) => {
  if (isFetchingOutlets || isLoading) {
    return
  }

  isLoading = true
  const countryCode = storageHelper.getParentEntityCountry() ?? ""

  const portalRetryTimeout = getGrabXValueFromLocalStorage<number>(
    MerchantPortalFeatureFlag.portalRetryTimeout,
  )

  const t0 = performance.now()
  if (!disableGlobalLoading) {
    dispatch(actionCreator.global.showLoading())
  }
  let offset = 0
  let hasMore = false
  const limit = 100
  const errors = []

  do {
    // eslint-disable-next-line no-await-in-loop
    const resp = (await dispatch(
      actionCreator.menu.fetchStoreList({
        countryCode,
        offset,
        limit,
        append: hasMore,
        isWithItemPhotoCount: Config.enableItemPhotoCoverage,
        stopAfterFirstPage,
      }) as AnyAction,
    )) as AxiosAction<FetchStoreListResponse>

    if (resp?.type.endsWith("_FAIL")) {
      const errorStatus = (resp as any)?.error.response?.status
      /**
       * retry if got 429 (Too Many Requests)
       */
      if (errorStatus === 429 && portalRetryTimeout) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(portalRetryTimeout)
        continue
      }

      const errorEventReason = getResponseErrorEventReason(resp)
      errors.push(errorEventReason)
    }
    offset += limit
    hasMore = resp?.payload?.data?.metadata?.hasMore ?? false
    if (!hasMore || stopAfterFirstPage) {
      break
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(1000)
  } while (hasMore)

  const t1 = performance.now()

  let labels = {
    LOAD_TIME: t1 - t0,
    LOADING_STATUS: "SUCCESS",
    REASON: "",
    ERROR_MSG: "",
  }

  if (errors?.length > 0) {
    labels = {
      ...labels,
      LOADING_STATUS: "FAILURE",
      REASON: errors.join(","),
    }
  }

  if (onFetched) {
    onFetched(labels)
  }

  if (!disableGlobalLoading) {
    dispatch(actionCreator.global.hideLoading())
  }

  isLoading = false
}

function useFetchAllStoreList() {
  const dispatch = useDispatch()
  const {
    menu: { isFetchingOutlets },
  } = useSelector((s: AppState) => s)
  const fetchAllStoreList = useMemo(
    () => fetchAllStoreListFactory(dispatch, isFetchingOutlets),
    [dispatch, isFetchingOutlets],
  )
  return fetchAllStoreList
}

export default useFetchAllStoreList
